import { scanCommentOrString } from './CsCommentStringScanner';

const PUNCTUATION = new Set(['{', '}', '(', ')', '<', '>', ',', ';', ':', '.', '=', '[', ']', '?']);

/**
 * Finalizes an accumulated `///` doc-comment block into docComments, keyed by
 * the token index it precedes (the first real token after the comment block —
 * i.e. tokens.length at the moment of flushing, since nothing else gets
 * pushed while a doc-block is being accumulated). Extracts <summary>...</summary>
 * if present, otherwise falls back to the raw joined comment text.
 * @param {string[]} docBuffer
 * @param {{tokenIndex: number, summary: string}[]} docComments
 * @param {number} tokenIndex
 */
function flushDocBuffer(docBuffer, docComments, tokenIndex) {
  if (!docBuffer.length) return;
  const raw = docBuffer.join('\n');
  const summaryMatch = raw.match(/<summary>([\s\S]*?)<\/summary>/i);
  const text = summaryMatch ? summaryMatch[1] : raw;
  const collapsed = text.split('\n').map(function trimLine(l) { return l.trim(); }).filter(Boolean).join(' ').trim();
  // VS2022's own XMLDoc hint viewer doesn't break on physical newlines, so
  // authors insert a literal <br/> for an intentional line break — turn that
  // into a real newline here instead of showing it as literal text.
  const cleaned = collapsed.replace(/\s*<br\s*\/?>\s*/gi, '\n').trim();
  if (cleaned) {
    docComments.push({ tokenIndex, summary: cleaned });
  }
}

/**
 * Tokenizes C# source into identifier/number/string/punctuation/other tokens.
 * Strips comments and whitespace entirely (not emitted as tokens). Strings
 * (incl. verbatim `@"..."` and interpolated `$"..."`) collapse to an opaque
 * '""' value — their content never matters for a class-diagram overview.
 *
 * `///` XMLDoc comments are the one exception — captured separately into
 * `docComments` (not emitted as tokens, so structural parsing in
 * CsTypeScanner/CsMemberScanner is unaffected) so CsClassParser can attach a
 * description to the declaration each doc-block precedes.
 * @param {string} source
 * @returns {{tokens: {type: string, value: string}[], docComments: {tokenIndex: number, summary: string}[]}}
 */
export function tokenizeCs(source) {
  const tokens = [];
  const docComments = [];
  let docBuffer = [];
  let i = 0;
  const n = source.length;

  while (i < n) {
    const ch = source[i];

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    const span = scanCommentOrString(source, i);
    if (span) {
      if (span.kind === 'xmlDocLine') {
        let text = source.slice(i + 3, span.end);
        if (text.startsWith(' ')) text = text.slice(1);
        docBuffer.push(text.trimEnd());
        i = span.end;
        continue;
      }

      // any other comment/string ends a pending doc-comment block
      flushDocBuffer(docBuffer, docComments, tokens.length);
      docBuffer = [];

      if (span.kind === 'lineComment' || span.kind === 'blockComment') {
        i = span.end;
        continue;
      }

      // string or char literal — collapse to opaque token, same as before
      tokens.push({ type: span.kind === 'char' ? 'char' : 'string', value: span.kind === 'char' ? "''" : '""' });
      i = span.end;
      continue;
    }

    if (docBuffer.length) {
      flushDocBuffer(docBuffer, docComments, tokens.length);
      docBuffer = [];
    }

    if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < n && /[A-Za-z0-9_]/.test(source[j])) j += 1;
      tokens.push({ type: 'identifier', value: source.slice(i, j) });
      i = j;
      continue;
    }

    if (/[0-9]/.test(ch)) {
      let j = i + 1;
      while (j < n && /[0-9.]/.test(source[j])) j += 1;
      tokens.push({ type: 'number', value: source.slice(i, j) });
      i = j;
      continue;
    }

    if (PUNCTUATION.has(ch)) {
      tokens.push({ type: 'punctuation', value: ch });
      i += 1;
      continue;
    }

    tokens.push({ type: 'other', value: ch });
    i += 1;
  }

  flushDocBuffer(docBuffer, docComments, tokens.length);

  return { tokens, docComments };
}
